import { useMemo } from "react";
import { Check, X, Shield, ShieldAlert, ShieldCheck } from "lucide-react";

/**
 * Password Strength Meter Component
 * Shows visual feedback on password strength with requirements checklist
 */
export default function PasswordStrengthMeter({ password, showRequirements = true }) {
  // Calculate password strength and check requirements
  const analysis = useMemo(() => {
    if (!password) {
      return { score: 0, label: "", color: "gray", requirements: [] };
    }

    const requirements = [
      { 
        id: "length", 
        label: "At least 8 characters", 
        met: password.length >= 8 
      },
      { 
        id: "uppercase", 
        label: "One uppercase letter", 
        met: /[A-Z]/.test(password) 
      },
      { 
        id: "lowercase", 
        label: "One lowercase letter", 
        met: /[a-z]/.test(password) 
      },
      { 
        id: "number", 
        label: "One number", 
        met: /[0-9]/.test(password) 
      },
      { 
        id: "special", 
        label: "One special character (!@#$%^&*)", 
        met: /[!@#$%^&*(),.?":{}|<>]/.test(password) 
      },
    ];

    const metCount = requirements.filter(r => r.met).length;
    
    // Calculate score (0-100)
    let score = 0;
    
    // Base score from requirements (max 60)
    score += metCount * 12;
    
    // Bonus for length (max 20)
    if (password.length >= 12) score += 20;
    else if (password.length >= 10) score += 15;
    else if (password.length >= 8) score += 10;
    
    // Bonus for variety (max 20)
    const uniqueChars = new Set(password).size;
    if (uniqueChars >= 10) score += 20;
    else if (uniqueChars >= 8) score += 15;
    else if (uniqueChars >= 6) score += 10;
    
    // Cap at 100
    score = Math.min(score, 100);
    
    // Determine label and color
    let label, color;
    if (score < 25) {
      label = "Weak";
      color = "red";
    } else if (score < 50) {
      label = "Fair";
      color = "orange";
    } else if (score < 75) {
      label = "Good";
      color = "yellow";
    } else {
      label = "Strong";
      color = "green";
    }

    return { score, label, color, requirements };
  }, [password]);

  if (!password) return null;

  const colorClasses = {
    red: {
      bar: "bg-red-500",
      text: "text-red-600",
      bg: "bg-red-50",
      icon: ShieldAlert
    },
    orange: {
      bar: "bg-orange-500",
      text: "text-orange-600",
      bg: "bg-orange-50",
      icon: Shield
    },
    yellow: {
      bar: "bg-yellow-500",
      text: "text-yellow-600",
      bg: "bg-yellow-50",
      icon: Shield
    },
    green: {
      bar: "bg-green-500",
      text: "text-green-600",
      bg: "bg-green-50",
      icon: ShieldCheck
    },
    gray: {
      bar: "bg-gray-300",
      text: "text-gray-500",
      bg: "bg-gray-50",
      icon: Shield
    }
  };

  const colors = colorClasses[analysis.color];
  const IconComponent = colors.icon;

  return (
    <div className="mt-2 space-y-2" data-testid="password-strength-meter">
      {/* Strength Bar */}
      <div className="flex items-center gap-2">
        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ${colors.bar}`}
            style={{ width: `${analysis.score}%` }}
          />
        </div>
        <div className={`flex items-center gap-1 ${colors.text} text-sm font-medium min-w-[70px]`}>
          <IconComponent className="w-4 h-4" />
          {analysis.label}
        </div>
      </div>

      {/* Requirements Checklist */}
      {showRequirements && (
        <div className={`p-3 rounded-lg ${colors.bg}`}>
          <p className="text-xs font-medium text-gray-600 mb-2">Password requirements:</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
            {analysis.requirements.map((req) => (
              <div
                key={req.id}
                className={`flex items-center gap-1.5 text-xs ${
                  req.met ? "text-green-600" : "text-gray-500"
                }`}
              >
                {req.met ? (
                  <Check className="w-3.5 h-3.5" />
                ) : (
                  <X className="w-3.5 h-3.5" />
                )}
                <span>{req.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Export helper to check if password meets minimum requirements
export function isPasswordStrong(password) {
  if (!password || password.length < 8) return false;
  
  const hasUppercase = /[A-Z]/.test(password);
  const hasLowercase = /[a-z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  
  // Require at least 3 of 4 criteria (length + 2 more)
  const criteriaCount = [hasUppercase, hasLowercase, hasNumber].filter(Boolean).length;
  return criteriaCount >= 2;
}
